import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Clock, AlertOctagon, CheckSquare, FileText } from 'lucide-react';
interface CaptiveClaimsGovernanceProps {
  onNavigate?: (dashboard: string) => void;
}
// Mock Data
const claimsAging = [{
  bucket: '0-30 Days',
  count: 45
}, {
  bucket: '31-90 Days',
  count: 32
}, {
  bucket: '91-180 Days',
  count: 18
}, {
  bucket: '180-365 Days',
  count: 12
}, {
  bucket: '>365 Days',
  count: 5
}];
const largeLosses = [{
  id: 'CLM-001',
  desc: 'Fleet Collision - Multi Vehicle',
  reserve: 450000,
  status: 'Open'
}, {
  id: 'CLM-002',
  desc: 'Warehouse Fire - Partial',
  reserve: 280000,
  status: 'Open'
}, {
  id: 'CLM-003',
  desc: 'Liability - Slip & Fall',
  reserve: 120000,
  status: 'Litigated'
}];
const slaBreaches = [{
  process: 'First Notification',
  target: '24h',
  actual: '98%',
  status: 'ok'
}, {
  process: 'Coverage Decision',
  target: '5 Days',
  actual: '92%',
  status: 'warning'
}, {
  process: 'Payment Issue',
  target: '48h',
  actual: '99%',
  status: 'ok'
}];
const COLORS = {
  bar: '#9333EA'
};
export function CaptiveClaimsGovernance({
  onNavigate
}: CaptiveClaimsGovernanceProps) {
  return <div className="space-y-6 animate-in" data-id="element-416">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-417">
        <div data-id="element-418">
          <h1 className="text-2xl font-bold text-black" data-id="element-419">Claims & Governance</h1>
          <p className="text-neutral-500 mt-1" data-id="element-420">
            SLA monitoring, large loss tracking, and governance
          </p>
        </div>
      </div>

      {/* Top Row: KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-421">
        <div className="bg-white p-4 rounded-lg border border-neutral-200 shadow-sm" data-id="element-422">
          <div className="flex justify-between items-start mb-2" data-id="element-423">
            <p className="text-xs font-medium text-neutral-500 uppercase" data-id="element-424">
              48h Acknowledgement
            </p>
            <CheckSquare size={16} className="text-green-500" data-id="element-425" />
          </div>
          <h3 className="text-2xl font-bold text-black" data-id="element-426">98.5%</h3>
          <p className="text-xs text-neutral-400" data-id="element-427">Target: 95%</p>
        </div>

        <div className="bg-white p-4 rounded-lg border border-neutral-200 shadow-sm" data-id="element-428">
          <div className="flex justify-between items-start mb-2" data-id="element-429">
            <p className="text-xs font-medium text-neutral-500 uppercase" data-id="element-430">
              Avg Days Open
            </p>
            <Clock size={16} className="text-purple-500" data-id="element-431" />
          </div>
          <h3 className="text-2xl font-bold text-black" data-id="element-432">47 Days</h3>
          <p className="text-xs text-neutral-400" data-id="element-433">Across all open claims</p>
        </div>

        <div className="bg-white p-4 rounded-lg border border-neutral-200 shadow-sm" data-id="element-434">
          <div className="flex justify-between items-start mb-2" data-id="element-435">
            <p className="text-xs font-medium text-neutral-500 uppercase" data-id="element-436">
              Repudiation Approval Lag
            </p>
            <AlertOctagon size={16} className="text-pink-500" data-id="element-437" />
          </div>
          <h3 className="text-2xl font-bold text-black" data-id="element-438">3.2 Days</h3>
          <p className="text-xs text-neutral-400" data-id="element-439">
            Avg time to approve repudiation
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-440">
        {/* Claims Aging */}
        <div className="bg-white p-6 rounded-lg border border-neutral-200 shadow-sm" data-id="element-441">
          <h3 className="text-lg font-medium text-black mb-4" data-id="element-442">
            Claims Aging Histogram
          </h3>
          <div className="h-80" data-id="element-443">
            <ResponsiveContainer width="100%" height="100%" data-id="element-444">
              <BarChart data={claimsAging} data-id="element-445">
                <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-446" />
                <XAxis dataKey="bucket" tick={{
                fontSize: 12
              }} data-id="element-447" />
                <YAxis data-id="element-448" />
                <Tooltip data-id="element-449" />
                <Bar dataKey="count" fill={COLORS.bar} radius={[4, 4, 0, 0]} data-id="element-450" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Large Loss Tracker */}
        <div className="bg-white p-6 rounded-lg border border-neutral-200 shadow-sm" data-id="element-451">
          <h3 className="text-lg font-medium text-black mb-4" data-id="element-452">
            Large Loss Tracker (&gt;£100k)
          </h3>
          <div className="space-y-4" data-id="element-453">
            {largeLosses.map(loss => <div key={loss.id} className="p-4 border border-neutral-100 rounded-lg flex justify-between items-center" data-id="element-454">
                <div data-id="element-455">
                  <div className="flex items-center gap-2" data-id="element-456">
                    <span className="font-bold text-black" data-id="element-457">{loss.id}</span>
                    <span className="px-2 py-0.5 bg-neutral-100 text-neutral-600 text-xs rounded-full" data-id="element-458">
                      {loss.status}
                    </span>
                  </div>
                  <p className="text-sm text-neutral-600 mt-1" data-id="element-459">{loss.desc}</p>
                </div>
                <div className="text-right" data-id="element-460">
                  <p className="text-sm text-neutral-500" data-id="element-461">Reserve</p>
                  <p className="text-lg font-bold text-black" data-id="element-462">
                    £{loss.reserve.toLocaleString()}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </div>

      {/* SLA Breaches */}
      <div className="bg-white p-6 rounded-lg border border-neutral-200 shadow-sm" data-id="element-463">
        <h3 className="text-lg font-medium text-black mb-4" data-id="element-464">
          SLA Performance & Breaches
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-465">
          {slaBreaches.map((sla, idx) => <div key={idx} className="p-4 bg-neutral-50 rounded-lg" data-id="element-466">
              <div className="flex justify-between items-start mb-2" data-id="element-467">
                <h4 className="font-medium text-black" data-id="element-468">{sla.process}</h4>
                <div className={`w-2 h-2 rounded-full ${sla.status === 'ok' ? 'bg-green-500' : 'bg-amber-500'}`} data-id="element-469" />
              </div>
              <div className="flex justify-between text-sm" data-id="element-470">
                <span className="text-neutral-500" data-id="element-471">Target: {sla.target}</span>
                <span className="font-bold text-black" data-id="element-472">{sla.actual}</span>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}